import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';

import { SharedModule } from '@app/shared/shared.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { ChangeStepService } from '@app/core/components/stepper/service/change-step.service';
import { ChangeDeviceService } from '@app/shared/services/change-device.service';
import { SaveDataService } from '@app/shared/services/save-data.service';

import { SelectButtonOptionClickEvent } from 'primeng/selectbutton';
import { CalendarModule } from 'primeng/calendar';

import { LabelCode } from '@app/shared/interfaces/label-code.interface';
import { CoreModule } from '@app/core/core.module';
import { AppService } from '@app/app.service';
import { ContractType } from '@app/shared/enums/contract-type';
import { PrimeNGConfig } from 'primeng/api';

@Component({
    selector: 'app-contract-type',
    standalone: true,
    imports: [CoreModule, CommonModule, SharedModule, TranslateModule],
    templateUrl: './contract-type.component.html',
    styleUrl: './contract-type.component.scss',
})
export class ContractTypeComponent implements AfterViewInit {

    private appData = inject(AppService);

    continueButton = 'common.buttons.continueButton';
    backButton = 'common.buttons.backButton';

    contractTypeTitle = 'features.contractType.contractTypeTitle';
    contractTypeSubtitle = 'features.contractType.contractTypeSubtitle';
    monthlyNetIncomeLabel = 'features.contractType.monthlyNetIncomeLabel';
    temporaryWorkingStartDateLabel = 'features.contractType.temporaryWorkingStartDateLabel';
    temporaryWorkingEndDateLabel = 'features.contractType.temporaryWorkingEndDateLabel';
    moreOptions = 'features.contractType.moreOptions';
    lessOptions = 'features.contractType.lessOptions';

    backButtonDisabled = false;
    continueButtonDisabled = true;

    isPortraitDevice = false;

    formGroup!: FormGroup;

    date = Date;
    selectedWorkSituationOption!: LabelCode;
    isStableWorkContractType: boolean = false;
    showForm: boolean = false;
	showMoreWorkContractType = false;

    workContractTypeOptions: any;

    workContractTypeOptionsExtra = [
        { label: 'Fijo', code: ContractType.FIXED },
        { label: 'Temporal', code: ContractType.EVENTUAL },
        { label: 'Fin de obra', code: ContractType.END_OF_WORK },
        { label: 'ERTE', code: ContractType.ERTE }
    ];

    // Opciones filtradas
    filteredworkContractTypeOptions = this.workContractTypeOptionsExtra.filter(option => (option.code == ContractType.FIXED || option.code == ContractType.EVENTUAL));

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private changeDeviceService: ChangeDeviceService,
        private saveDataService: SaveDataService,
        private changeStepService: ChangeStepService,
        private fb: FormBuilder,
        private primengConfig: PrimeNGConfig,
        private translateService: TranslateService
    ) {
        this.changeStepService.changeStep(9);
    }

    ngOnInit() {
        this.isPortraitDevice = this.changeDeviceService.isPortraitDevice;
        this.changeDeviceService.changedDevice.subscribe((value) => {
            this.isPortraitDevice = value;
        });

        this.continueButtonDisabled = false;

        this.createForm();

        this.workContractTypeOptions = this.filteredworkContractTypeOptions;

        this.setValuesForm(this.appData.data.userData.contractType);

        this.formIsValid();
    }

    createForm() {

        if (this.showForm) {
            if (this.isStableWorkContractType) {
                this.formGroup = this.fb.group({
                    workContractType: new FormControl(this.selectedWorkSituationOption ? this.selectedWorkSituationOption.code : ContractType.FIXED, [Validators.required]),
                    stableWorkingStartDate: new FormControl(null, [Validators.required]),
                    monthlyNetIncome: new FormControl('', [Validators.required, Validators.min(1), Validators.minLength(1), Validators.pattern(/^-?(0|[1-9]\d*)?$/)]),
                });
            } else {
                this.formGroup = this.fb.group({
                    workContractType: new FormControl(this.selectedWorkSituationOption ? this.selectedWorkSituationOption.code : ContractType.EVENTUAL, [Validators.required]),
                    temporaryWorkingStartDate: new FormControl(null, [Validators.required]),
                    temporaryWorkingEndDate: new FormControl(null, [Validators.required]),
                    monthlyNetIncome: new FormControl('', [Validators.required, Validators.min(1), Validators.minLength(1), Validators.pattern(/^-?(0|[1-9]\d*)?$/)]),
                }, { validators: this.dateRangeValidator() });
            }
        } else {
            this.formGroup = this.fb.group({
                workContractType: new FormControl(this.selectedWorkSituationOption ? this.selectedWorkSituationOption.code : '', [Validators.required]),
            });
        }
    }

    setValuesForm(value?: any) {
        if (value != undefined) {

            if(value.workContractType != undefined && value.workContractType != ''){
                // Emitir el evento
               const event: SelectButtonOptionClickEvent = {
                   originalEvent: new Event('click'),
                   option: this.workContractTypeOptions.find((option: { code: any; }) => option.code === value.workContractType)
               };
               this.onChangeWorkContractType(event);
           }

            if (this.showForm) {
                if (this.isStableWorkContractType) {
                    this.formGroup = this.fb.group({
                        workContractType: new FormControl(value.workContractType ? value.workContractType : ContractType.FIXED, [Validators.required]),
                        stableWorkingStartDate: new FormControl(value.stableWorkingStartDate ? value.stableWorkingStartDate : null, [Validators.required]),
                        monthlyNetIncome: new FormControl(value.monthlyNetIncome ? value.monthlyNetIncome : '', [Validators.required, Validators.minLength(1), Validators.pattern(/^-?(0|[1-9]\d*)?$/)]),
                    });
                } else {
                    this.formGroup = this.fb.group({
                        workContractType: new FormControl(value.workContractType ? value.workContractType : ContractType.EVENTUAL, [Validators.required]),
                        temporaryWorkingStartDate: new FormControl(value.temporaryWorkingStartDate ? value.temporaryWorkingStartDate : null, [Validators.required]),
                        temporaryWorkingEndDate: new FormControl(value.temporaryWorkingEndDate ? value.temporaryWorkingEndDate : null, [Validators.required]),
                        monthlyNetIncome: new FormControl(value.monthlyNetIncome ? value.monthlyNetIncome : '', [Validators.required, Validators.minLength(1), Validators.pattern(/^-?(0|[1-9]\d*)?$/)]),
                    }, { validators: this.dateRangeValidator() });
                }
            } else {
                this.formGroup = this.fb.group({
                    workContractType: new FormControl(value.workContractType ? value.workContractType.code : '', [Validators.required]),
                });
            }
        }
    }

    // Traducimos los calendarios
    ngAfterViewInit() {
        this.translateChange('es')
    }

    translateChange(lang: string) {
        this.translateService.use(lang)
        this.translateService.get('primeng').subscribe((res) => this.primengConfig.setTranslation(res))
    }

    //-------------------------//
    //-------ON CHANGES--------//
    //-------------------------//

    onChangeWorkContractType(event: SelectButtonOptionClickEvent | undefined) {
        this.showForm = true;
        if (event != undefined)
            this.selectedWorkSituationOption = event.option;
        console.log(this.selectedWorkSituationOption);
        if (this.selectedWorkSituationOption.code == ContractType.FIXED || this.selectedWorkSituationOption.code == ContractType.END_OF_WORK) {
            this.isStableWorkContractType = true;
        } else if (this.selectedWorkSituationOption.code == ContractType.EVENTUAL || this.selectedWorkSituationOption.code == ContractType.ERTE) {
            this.isStableWorkContractType = false;
        }
        this.createForm();
        this.formIsValid();
    }

    onChangeMonthlyNetIncome(event: Event) {
        this.formIsValid();
    }

    onChangeDate(event: CalendarModule) {
        this.formIsValid();
    }

    onClickWorkContractType (event:boolean) {
        if(event){
            this.workContractTypeOptions = this.workContractTypeOptionsExtra;
        } else {
            this.workContractTypeOptions = this.filteredworkContractTypeOptions;
        }

        this.showMoreWorkContractType = event;
    }

    //-------------------------//
    //-------VALIDATION--------//
    //-------------------------//

    dateRangeValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
          const startDate = control.get('temporaryWorkingStartDate')?.value;
          const endDate = control.get('temporaryWorkingEndDate')?.value;

          if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            return { dateRangeInvalid: true };
          }

          return null;
        };
      }

    formIsValid() {
        this.continueButtonDisabled = !this.formGroup.valid;
    }

    //-------------------------//
    //--------NAVIGATION-------//
    //-------------------------//

    onClicButtonBack() {
        this.saveDataService.saveDataLocal(this.formGroup.value, 'contract-type');
        this.router.navigate(['/main/step/working-situation'], { relativeTo: this.route });
    }

    onClicButtonContinue() {
        this.saveDataService.saveDataLocal(this.formGroup.value, 'contract-type');
        this.router.navigate(['/main/step/additional-data-first'], { relativeTo: this.route });
    }

    ngOnDestroy() {}

}
